import React, { useEffect, useMemo, useState } from 'react';
import { CustomSelect } from 'web-library';
import { MenuItems } from './model';
import { KeyValueResponse } from '../../api';
import { useGetAccountStatusQuery } from '../../api/account-management/useGetAccountStatusQuery';
import { SelectChangeEvent } from '@mui/material';

export interface AccountStatusMultiSelectProps {
  /** to handle select on change event
   * */
  onChange?: (event: KeyValueResponse[]) => void;

  /** to define select control selected value
   * */
  value: string[];

  /** to define option label
   * */
  label: string;

  /** to define fitler option spacing
   * */
  filterOptionSpacing: string;

  /**
   * to determine if candidate specific filters should be queried
   */
  isCandidate: boolean;
}

/** reusable select component for account status option
 * */
const AccountStatusMultiSelect: React.FC<AccountStatusMultiSelectProps> = (
  props: AccountStatusMultiSelectProps
) => {
  const {
    onChange = (_event: KeyValueResponse[]) => {
      // do nothing
    },
    value = [],
    label,
    filterOptionSpacing,
    isCandidate,
  } = props;
  const [accountStatus, setAccountStatus] = useState<KeyValueResponse[]>([]);

  const { refetch } = useGetAccountStatusQuery({
    isCandidate,
    queryOptions: {
      enabled: true,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      onSuccess: (responseData) => {
        if (responseData && responseData.length > 0) {
          setAccountStatus(responseData);
        }
      },
    },
  });

  /** reform data into CustomSelect MenuItems
   * */
  const dropDownData = useMemo<MenuItems[]>(() => {
    if (!accountStatus) {
      return [];
    }
    return accountStatus.map((option) => ({
      children: option.description,
      value: option.code,
      key: `k-${option.code}`,
    }));
  }, [accountStatus]);

  /** handle select on Change
   * */
  const onSelectOptionChange = async (selectedValue: any[] | unknown) => {
    if (accountStatus && accountStatus.length > 0 && Array.isArray(selectedValue)) {
      onChange(accountStatus.filter((status) => selectedValue.includes(status.code)));
    }
  };

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <CustomSelect
      className={filterOptionSpacing}
      displayEmpty
      variant="filled"
      multiple
      label={label}
      labelId="inputSelectLabel-accountStatus"
      id="inputSelect-accountStatus"
      onChange={(e: SelectChangeEvent<unknown>) => {
        onSelectOptionChange(e.target.value);
        window.dispatchEvent(new CustomEvent('resize'));
      }}
      menuItems={dropDownData}
      value={value}
      renderValue={(selected: unknown | string[]) =>
        Array.isArray(selected) && selected.length > 0
          ? accountStatus
              .filter((status) => selected.includes(status.code))
              .map((s) => s.description)
              .join(', ')
          : 'Select status'
      }
    />
  );
};

export default AccountStatusMultiSelect;
